import axiosInstance from "../config/axiosInstance";
import { ApiRoutes } from "../constants/ApiRoutes";
import { QueryKeys } from "../constants/QueryKeys";
import { MutateKeys } from "../constants/MutateKeys";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { generatePath } from "react-router-dom";
import { cleanParams } from "../Utilities/queryParamUtils";
import { ArchiveKeys } from "../enums/ArchiveActionKeys";
import {
  AddQuarryParams,
  FetchQuarryListParams,
  QuarryListServiceType,
  QuarryMaterialCategoryType,
  QuarryMaterialFilterServiceType,
  QuarryMaterialParamsType,
  QuarryMetaServiceType,
  QuarryResponse,
  AddMaterialNamePayloadType,
  AddQuarryMaterialPayloadType,
  QuarryMapApiResponse,
  ContactPayload,
} from "../Types/quarryDatabaseTypes";
import { DatabaseMetricsKey, materialProfile } from "../enums/DatabaseEnums";

const QuarryDatabaseService = () => {
  const queryClient = useQueryClient();
  const {
    DATABASE_META_ROUTE,
    FETCH_QUARRY_LIST_ROUTE,
    FETCH_QUARRY_FILTER_META_ROUTE,
    ADD_NEW_QUARRY_ROUTE,
    FETCH_QUARRY_DETAILS_ROUTE,
    QUARRY_MATERIAL_TABLE_LISTING,
    ADD_QUARRY_MATERIAL_META,
    ARCHIVE_DATABASE_ENTRY,
    ADD_NEW_MATERIAL_NAME,
    ADD_NEW_QUARRY_MATERIAL,
    GET_AFFECTED_JOBS_FOR_QUARRY_COUNT,
    GET_MATERIAL_IMAGES,
    DOWNLOAD_ALL_MATERIAL_IMAGES,
    FETCH_QUARRY_HEADER_DETAILS,
    EDIT_QUARRY_MATERIAL,
    SYNC_QUICKBOOKS_QUARRY_ROUTE,
    DELETE_MATERIAL_IMAGE,
    ADD_MATERIAL_IMAGES,
    UPDATE_DEPENDENTS_TABLE,
    UPDATE_QUARRY_PROFILE_NOTES,
    GET_QUARRIES_PRESENT_MATERIALS,
  } = ApiRoutes;

  const {
    FETCH_QUARRY_META_KEY,
    FETCH_QUARRY_LIST_KEY,
    FETCH_QUARRY_MATERIAL_TYPE_KEY,
    FETCH_QUARRY_MATERIAL_CATEGORY_KEY,
    FETCH_QUARRY_DETAILS_KEY,
    FETCH_QUARRY_MATERIAL_CATEGORY_LIST_KEY,
    FETCH_ADD_QUARRY_MATERIAL_CATEGORY_KEY,
    FETCH_ADD_QUARRY_MATERIAL_SUBCATEGORY_KEY,
    FETCH_ADD_QUARRY_MATERIAL_NAME_KEY,
    GET_MATERIAL_IMAGES_KEY,
    FETCH_QUARRY_HEADER_DETAILS_KEY,
    GET_QUARRIES_PRESENT_MATERIALS_KEY,
  } = QueryKeys;

  const {
    ADD_NEW_QUARRY_KEY,
    FETCH_QUARRY_MATERIAL_SUBCATEGORY_KEY,
    ADD_NEW_MATERIAL_NAME_KEY,
    ADD_NEW_QUARRY_MATERIAL_KEY,
    EDIT_QUARRY_MATERIAL_KEY,
    DELETE_QUARRY_MATERIAL_KEY,
    DOWNLOAD_ALL_MATERIAL_IMAGES_KEY,
    SYNC_QUARRY_QUICKBOOKS_KEY,
    DELETE_MATERIAL_IMAGE_KEY,
    ADD_MATERIAL_IMAGES_KEY,
    SET_CONTACT_STATUS_KEY,
    UPDATE_QUARRY_NOTES_KEY,
    GET_AFFECTED_JOBS_FOR_QUARRY_COUNT_KEY
  } = MutateKeys;

  const FetchQuarryDatabaseMeta = (params?: FetchQuarryListParams) => {
    const fetchQuarryDatabaseMeta =
      async (): Promise<QuarryMetaServiceType> => {
        const cleanedParams = cleanParams(params);
        const url = `${DATABASE_META_ROUTE}?type=${DatabaseMetricsKey.QUARRY_METRICS}`;
        const { data } = await axiosInstance.post<QuarryMetaServiceType>(url, {
          ...cleanedParams,
        });
        return data;
      };
    return useQuery({
      queryKey: [FETCH_QUARRY_META_KEY, params],
      queryFn: fetchQuarryDatabaseMeta,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    });
  };

  const FetchQuarryList = (
    params?: FetchQuarryListParams,
    pageSizeChanged: boolean,
    enabled: boolean
  ) => {
    const fetchQuarryList = async (): Promise<QuarryListServiceType> => {
      const cleanedParams = cleanParams({...params, setDefaultPageSize: pageSizeChanged });
      const { data } = await axiosInstance.post<QuarryListServiceType>(
        FETCH_QUARRY_LIST_ROUTE,
        cleanedParams
      );
      return data;
    };
    return useQuery({
      queryKey: [FETCH_QUARRY_LIST_KEY, params],
      queryFn: fetchQuarryList,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: enabled,
    });
  };

  const FetchQuarryMaterialType = (params?: QuarryMaterialParamsType) => {
    const fetchQuarryMaterialType =
      async (): Promise<QuarryMaterialFilterServiceType> => {
        const url = generatePath(FETCH_QUARRY_FILTER_META_ROUTE, {
          id: "type",
        });
        const cleanedParams = cleanParams(params);
        const { data } =
          await axiosInstance.post<QuarryMaterialFilterServiceType>(
            url,
            cleanedParams
          );
        return data;
      };
    return useQuery({
      queryKey: [FETCH_QUARRY_MATERIAL_TYPE_KEY, params],
      queryFn: fetchQuarryMaterialType,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    });
  };

  const FetchQuarryMaterialCategory = (params?: QuarryMaterialCategoryType) => {
    const fetchQuarryMaterialCategory =
      async (): Promise<QuarryMaterialFilterServiceType> => {
        const url = generatePath(FETCH_QUARRY_FILTER_META_ROUTE, {
          id: "category",
        });
        const cleanedParams = cleanParams(params);
        const { data } =
          await axiosInstance.post<QuarryMaterialFilterServiceType>(
            url,
            cleanedParams
          );
        return data;
      };
    return useQuery({
      queryKey: [FETCH_QUARRY_MATERIAL_CATEGORY_KEY, params],
      queryFn: fetchQuarryMaterialCategory,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    });
  };

  const AddNewQuarry = () => {
    const addNewQuarry = async (params: AddQuarryParams) => {
      const cleanedParams = cleanParams(params);
      const payload = {
        entityData: {
          ...cleanedParams,
        },
      };
      const { data } = await axiosInstance.post(ADD_NEW_QUARRY_ROUTE, payload);
      return data;
    };
    return useMutation({
      mutationFn: addNewQuarry,
      mutationKey: [ADD_NEW_QUARRY_KEY],
      onSuccess: () => queryClient.invalidateQueries([FETCH_QUARRY_LIST_KEY]),
    });
  };

  const EditQuarry = () => {
    const editQuarry = async (params: AddQuarryParams) => {
      const { id, ...rest } = params;
      const cleanedParams = cleanParams(rest);
      const payload = {
        entityData: {
          ...cleanedParams,
        },
      };
      const url = generatePath(FETCH_QUARRY_DETAILS_ROUTE, {
        id: id,
      });
      const { data } = await axiosInstance.put(url, payload);
      return data;
    };
    return useMutation({
      mutationFn: editQuarry,
      mutationKey: [ADD_NEW_QUARRY_KEY],
      onSuccess: () => queryClient.invalidateQueries([FETCH_QUARRY_LIST_KEY]),
    });
  };

  const FetchQuarryDetails = (id?: string, fromMap: boolean) => {
    const fetchQuarryDetails = async (): Promise<
      QuarryResponse | QuarryMapApiResponse
    > => {
      const url = generatePath(FETCH_QUARRY_DETAILS_ROUTE, {
        id: id,
      });
      const cleanedParams = cleanParams({
        fromMap: fromMap,
      });
      const { data } = await axiosInstance.get<
        QuarryResponse | QuarryMapApiResponse
      >(url, {
        params: cleanedParams,
      });
      return data;
    };
    return useQuery({
      queryKey: [FETCH_QUARRY_DETAILS_KEY, fromMap],
      queryFn: fetchQuarryDetails,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      enabled: !!id,
    });
  };
  const FetchQuarryProfileCategoryList = ({
    quarryId,
  }: {
    quarryId: string;
  }) => {
    const fetchQuarryProfileCategoryList = async () => {
      const payload = {
        type: materialProfile.CATEGORY,
      };
      const { data } = await axiosInstance.post(
        generatePath(QUARRY_MATERIAL_TABLE_LISTING, { id: quarryId }),
        payload
      );
      const formattedData = data?.categories?.map((category) => ({
        materialCategory: category,
        id: category,
      }));
      return formattedData;
    };

    return useQuery({
      queryKey: [FETCH_QUARRY_MATERIAL_CATEGORY_LIST_KEY],
      queryFn: fetchQuarryProfileCategoryList,
      refetchOnWindowFocus: false,
    });
  };

  const FetchQuarryProfileSubCategoryAndMaterials = ({
    quarryId,
  }: {
    quarryId: string;
  }) => {
    const fetchQuarryProfileSubCategoryAndMaterials = async (
      categoryValue: string
    ) => {
      const payload = {
        type: materialProfile.MATERIAL,
        category: categoryValue,
      };
      const { data } = await axiosInstance.post(
        generatePath(QUARRY_MATERIAL_TABLE_LISTING, { id: quarryId }),
        payload
      );
      const formattedData = Object.entries(data?.materials)?.map(
        ([key, content]) => ({ title: key, key: key, content: content })
      );
      return formattedData;
    };

    return useMutation({
      mutationKey: [FETCH_QUARRY_MATERIAL_SUBCATEGORY_KEY],
      mutationFn: fetchQuarryProfileSubCategoryAndMaterials,
    });
  };

  const FetchMaterialCategoryMeta = ({ enabled }: { enabled: boolean }) => {
    const fetchMaterialCategoryMeta = async () => {
      const { data } = await axiosInstance.post(
        generatePath(ADD_QUARRY_MATERIAL_META, {
          id: materialProfile.CATEGORY,
        })
      );
      const formattedData = data?.categories?.map((value) => ({
        label: value,
        value: value,
      }));
      return formattedData;
    };

    return useQuery({
      queryKey: [FETCH_ADD_QUARRY_MATERIAL_CATEGORY_KEY],
      queryFn: fetchMaterialCategoryMeta,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: !!enabled,
    });
  };

  const FetchMaterialSubCategoryMeta = ({
    categoryName,
    enabled,
  }: {
    categoryName?: string;
    enabled?: boolean;
  }) => {
    const fetchMaterialSubCategoryMeta = async () => {
      const payload = {
        category: categoryName,
      };
      const { data } = await axiosInstance.post(
        generatePath(ADD_QUARRY_MATERIAL_META, {
          id: materialProfile.SUB_CATEGORY,
        }),
        payload
      );
      return data?.subCategories;
    };

    return useQuery({
      queryKey: [FETCH_ADD_QUARRY_MATERIAL_SUBCATEGORY_KEY, categoryName],
      queryFn: fetchMaterialSubCategoryMeta,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: !!enabled,
    });
  };

  const FetchMaterialNameMeta = ({
    categoryName,
    subCategoryName,
    enabled,
  }: {
    categoryName?: string;
    subCategoryName?: string;
    enabled?: boolean;
  }) => {
    const fetchMaterialNameMeta = async () => {
      const payload = {
        category: categoryName,
        subCategory: subCategoryName,
      };
      const { data } = await axiosInstance.post(
        generatePath(ADD_QUARRY_MATERIAL_META, {
          id: materialProfile.MATERIAL_NAME,
        }),
        payload
      );
      return data?.materials;
    };

    return useQuery({
      queryKey: [FETCH_ADD_QUARRY_MATERIAL_NAME_KEY, subCategoryName],
      queryFn: fetchMaterialNameMeta,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: !!enabled,
    });
  };

  const AddNewMaterialName = (subCategory: string) => {
    const addNewMaterialName = async (
      payloadData: AddMaterialNamePayloadType
    ) => {
      const payload = {
        entityData: {
          ...payloadData,
        },
      };
      const { data } = await axiosInstance.post(ADD_NEW_MATERIAL_NAME, payload);
      return data;
    };

    return useMutation({
      mutationKey: [ADD_NEW_MATERIAL_NAME_KEY],
      mutationFn: addNewMaterialName,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [FETCH_ADD_QUARRY_MATERIAL_NAME_KEY],
        });
      },
    });
  };

  const AddNewQuarryMaterial = ({ quarryId }: { quarryId: string }) => {
    const addNewQuarryMaterial = async (
      payloadData: AddQuarryMaterialPayloadType
    ) => {
      const payload = {
        entityData: {
          ...cleanParams(payloadData),
        },
      };
      const { data } = await axiosInstance.post(
        generatePath(ADD_NEW_QUARRY_MATERIAL, { id: quarryId }),
        payload
      );
      return data;
    };

    return useMutation({
      mutationKey: [ADD_NEW_QUARRY_MATERIAL_KEY],
      mutationFn: addNewQuarryMaterial,
    });
  };

  const GetAffectedJobsCount = () => {
    const getAffectedJobsCount = async (payloadData: {
      quarryId: string;
      revisedMaterialCost: string;
      materialValue: string;
      materialType: string;
    }) => {
      const payload = {
        ...cleanParams(payloadData),
      };
      const { data } = await axiosInstance.post(
        GET_AFFECTED_JOBS_FOR_QUARRY_COUNT,
        payload,
      );
      return data;
    };

    return useMutation({
      mutationKey: [GET_AFFECTED_JOBS_FOR_QUARRY_COUNT_KEY],
      mutationFn: getAffectedJobsCount,
    });
  };

  const EditQuarryMaterial = ({
    quarryId,
    entryId,
  }: {
    quarryId: string;
    entryId: string;
  }) => {
    const editQuarryMaterial = async (
      payloadData: AddQuarryMaterialPayloadType
    ) => {
      const payload = {
        entityData: {
          ...cleanParams(payloadData),
        },
      };
      const { data } = await axiosInstance.put(
        generatePath(EDIT_QUARRY_MATERIAL, {
          id: quarryId,
          materialId: entryId,
        }),
        payload
      );
      return data;
    };

    return useMutation({
      mutationKey: [EDIT_QUARRY_MATERIAL_KEY],
      mutationFn: editQuarryMaterial,
    });
  };

  const DeleteQuarryMaterial = () => {
    const deleteQuarryMaterial = async ({
      quarryId,
      materialId,
      notes,
    }: {
      quarryId: string;
      materialId: string;
      notes: string;
    }) => {
      const payload = {
        entityData: {
          quarryId: quarryId,
          deletedTable: ArchiveKeys.MATERIAL,
          notes: notes,
        },
      };
      const { data } = await axiosInstance.post(
        generatePath(ARCHIVE_DATABASE_ENTRY, { id: materialId }),
        payload
      );
      return data;
    };

    return useMutation({
      mutationKey: [DELETE_QUARRY_MATERIAL_KEY],
      mutationFn: deleteQuarryMaterial,
    });
  };

  const GetMaterialImages = ({
    quarryId,
    materialId,
    enabled,
  }: {
    quarryId: string;
    materialId: string;
    enabled: boolean;
  }) => {
    const getMaterialImages = async () => {
      const { data } = await axiosInstance.get(
        generatePath(GET_MATERIAL_IMAGES, {
          quarryId: quarryId,
          materialId: materialId,
        })
      );
      return data?.images;
    };

    return useQuery({
      queryKey: [GET_MATERIAL_IMAGES_KEY],
      queryFn: getMaterialImages,
      refetchOnWindowFocus: false,
      enabled: !!enabled,
    });
  };

  const DownloadAllMaterialImages = () => {
    const downloadAllMaterialImages = async ({
      quarryId,
      materialId,
    }: {
      quarryId: string;
      materialId: string;
    }) => {
      const { data } = await axiosInstance.get(
        generatePath(DOWNLOAD_ALL_MATERIAL_IMAGES, {
          quarryId: quarryId,
          materialId: materialId,
        })
      );
      return data?.downloadUrl;
    };

    return useMutation({
      mutationKey: [DOWNLOAD_ALL_MATERIAL_IMAGES_KEY],
      mutationFn: downloadAllMaterialImages,
    });
  };

  const FetchQuarryHeaderDetails = ({ quarryId }: { quarryId: string }) => {
    const fetchQuarryHeaderDetails = async () => {
      const { data } = await axiosInstance.get(
        generatePath(FETCH_QUARRY_HEADER_DETAILS, { id: quarryId })
      );
      return data?.data;
    };

    return useQuery({
      queryKey: [FETCH_QUARRY_HEADER_DETAILS_KEY, quarryId],
      queryFn: fetchQuarryHeaderDetails,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });
  };

  const SyncQuickBooks = () => {
    const syncQuickBooks = async () => {
      const { data } = await axiosInstance.post(SYNC_QUICKBOOKS_QUARRY_ROUTE);
      return data;
    };
    return useMutation({
      mutationFn: syncQuickBooks,
      mutationKey: [SYNC_QUARRY_QUICKBOOKS_KEY],
    });
  };
  const DeleteMaterialImage = ({ quarryId }: { quarryId: string }) => {
    const deleteMaterialImage = async ({
      materialId,
      entryId,
    }: {
      materialId: string;
      entryId: string;
    }) => {
      const { data } = axiosInstance.delete(
        generatePath(DELETE_MATERIAL_IMAGE, {
          quarryId: quarryId,
          materialId: materialId,
          entryId: entryId,
        })
      );
      return data;
    };

    return useMutation({
      mutationKey: [DELETE_MATERIAL_IMAGE_KEY],
      mutationFn: deleteMaterialImage,
    });
  };

  const AddMaterialPhotos = () => {
    const addMaterialPhotos = async (payloadData) => {
      const payload = {
        entityData: {
          ...cleanParams(payloadData),
        },
      };

      const { data } = await axiosInstance.post(ADD_MATERIAL_IMAGES, payload);
      return data;
    };

    return useMutation({
      mutationKey: [ADD_MATERIAL_IMAGES_KEY],
      mutationFn: addMaterialPhotos,
    });
  };

  const SetPrimaryContactStatus = () => {
    const setPrimaryContactStatus = async ({
      payloadData,
      sectionId,
      entityId,
    }: {
      payloadData: ContactPayload;
      sectionId: string;
      entityId: string;
    }) => {
      const payload = {
        entityData: {
          ...payloadData,
        },
      };
      const { data } = await axiosInstance.put(
        generatePath(UPDATE_DEPENDENTS_TABLE, {
          id: "contacts",
          entityId: entityId,
        }),
        payload,
        {
          params: {
            section: "quarry",
            section_id: sectionId,
          },
        }
      );
      return data;
    };

    return useMutation({
      mutationFn: setPrimaryContactStatus,
      mutationKey: [SET_CONTACT_STATUS_KEY],
    });
  };

  const UpdateQuarryNotes = ({ quarryId }: { quarryId: string }) => {
    const updateQuarryNotes = async (notes: string) => {
      const payload = {
        entityData: {
          notes: notes,
        },
      };
      const { data } = await axiosInstance.patch(
        generatePath(UPDATE_QUARRY_PROFILE_NOTES, { id: quarryId }),
        { ...payload }
      );
      return data;
    };

    return useMutation({
      mutationFn: updateQuarryNotes,
      mutationKey: [UPDATE_QUARRY_NOTES_KEY],
    });
  };

  const GetQuarryMaterialForDocuments = ({
    quarryId,
    enabled,
  }: {
    quarryId: string;
    enabled: boolean;
  }) => {
    const getQuarryMaterialForDocuments = async () => {
      const { data } = await axiosInstance?.get(
        generatePath(GET_QUARRIES_PRESENT_MATERIALS, { id: quarryId })
      );
      const formattedData = data?.materials?.map((item) => ({
        value: item.materialId,
        label: item?.material,
      }));
      return formattedData ?? [];
    };

    return useQuery({
      queryKey: [GET_QUARRIES_PRESENT_MATERIALS_KEY],
      queryFn: getQuarryMaterialForDocuments,
      refetchOnWindowFocus: false,
      enabled: !!enabled,
    });
  };

  return {
    FetchQuarryProfileCategoryList,
    FetchQuarryProfileSubCategoryAndMaterials,
    FetchQuarryDatabaseMeta,
    FetchQuarryList,
    FetchQuarryMaterialType,
    FetchQuarryMaterialCategory,
    AddNewQuarry,
    EditQuarry,
    FetchQuarryDetails,
    FetchMaterialCategoryMeta,
    FetchMaterialSubCategoryMeta,
    FetchMaterialNameMeta,
    AddNewMaterialName,
    AddNewQuarryMaterial,
    GetAffectedJobsCount,
    DeleteQuarryMaterial,
    GetMaterialImages,
    DownloadAllMaterialImages,
    FetchQuarryHeaderDetails,
    EditQuarryMaterial,
    SyncQuickBooks,
    AddMaterialPhotos,
    DeleteMaterialImage,
    SetPrimaryContactStatus,
    UpdateQuarryNotes,
    GetQuarryMaterialForDocuments,
  };
};

export default QuarryDatabaseService;
